<template>
  <div class="day-flag-popover-root">
    <div
      v-if="day"
      ref="popover"
      class="day-flag-popover"
      :class="'day-flag-popover--' + placement"
      :style="{top: top + 'px', left: left + 'px', visibility: positioned ? 'visible' : 'hidden'}"
      role="dialog"
    >
      <span class="day-flag-popover__arrow" :style="{left: arrowLeft + 'px'}"></span>

      <div class="day-flag-popover__header">
        <span v-if="isNote" class="day-flag-popover__icon day-flag-popover__icon--note">
          <i class="fas fa-comment-dots fa-fw"></i>
        </span>
        <span v-else class="day-flag-popover__icon" :class="'day-flag-popover__icon--' + (day.flagId !== 2 ? 'info' : 'danger')">
          <i class="fas fa-comment-dots fa-fw"></i>
        </span>
        <div class="day-flag-popover__heading">
          <div class="day-flag-popover__title">{{ isNote ? trans.popover.note_title : trans.popover.external_title }}</div>
          <div class="day-flag-popover__subtitle">
            <template v-if="!isNote">{{ trans.flag[day.flagId] }} · </template>{{ dayLabel }}
          </div>
        </div>
        <button type="button" class="day-flag-popover__close" :title="trans.popover.close" @click="close()">
          <i class="fal fa-times"></i>
        </button>
      </div>

      <template v-if="isNote">
        <div class="day-flag-popover__body">{{ day.note }}</div>
        <div class="day-flag-popover__footer">
          <span class="day-flag-popover__meta">
            <template v-if="day.noteAuthor">{{ day.noteAuthor }} · </template>{{ day.noteUpdatedAt | datetime('D. M. YYYY HH:mm') }}
          </span>
          <span v-if="!day.isLocked" class="day-flag-popover__actions">
            <button type="button" class="day-flag-popover__action" :title="trans.popover.edit" @click="edit()">
              <i class="far fa-pencil-alt"></i>
            </button>
          </span>
        </div>
      </template>

      <template v-else>
        <div class="day-flag-popover__body">{{ day.flagId === 3 || !day.flagReason ? '-' : day.flagReason }}</div>
      </template>
    </div>
  </div>
</template>

<script>
const GAP = 8;
const VIEWPORT_MARGIN = 8;
const ARROW_MARGIN = 14;

export default {
  name: 'day-flag-popover',
  data: function () {
    return {
      day: null,
      kind: null,
      anchor: null,
      top: 0,
      left: 0,
      arrowLeft: 0,
      placement: 'bottom',
      positioned: false
    }
  },

  props: {
    'trans': {
      type: Object
    }
  },

  computed: {
    isNote() {
      return this.kind === 'note';
    },
    dayLabel() {
      const label = moment(this.day.date).format('dddd D. M.');
      return label.charAt(0).toUpperCase() + label.slice(1);
    }
  },

  mounted() {
    // rendered in body, so the table overflow does not clip it
    document.body.appendChild(this.$el);
    document.addEventListener('mousedown', this.onDocumentMousedown, true);
    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener('scroll', this.close, true);
    window.addEventListener('resize', this.close);
  },

  beforeDestroy() {
    document.removeEventListener('mousedown', this.onDocumentMousedown, true);
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('scroll', this.close, true);
    window.removeEventListener('resize', this.close);
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }
  },

  methods: {
    toggle(anchor, day, kind) {
      if (this.anchor === anchor && this.day) {
        this.close();
        return;
      }

      this.anchor = anchor;
      this.day = day;
      this.kind = kind;
      this.positioned = false;
      this.$nextTick(() => this.updatePosition());
    },

    close() {
      this.day = null;
      this.kind = null;
      this.anchor = null;
    },

    updatePosition() {
      const popover = this.$refs.popover;
      if (!popover || !this.anchor) {
        return;
      }

      const rect = this.anchor.getBoundingClientRect();
      const width = popover.offsetWidth;
      const height = popover.offsetHeight;
      const anchorCenter = rect.left + rect.width / 2;

      this.placement = rect.bottom + GAP + height + VIEWPORT_MARGIN > window.innerHeight && rect.top - GAP - height >= VIEWPORT_MARGIN
        ? 'top' : 'bottom';
      this.top = this.placement === 'top' ? rect.top - GAP - height : rect.bottom + GAP;

      const maxLeft = window.innerWidth - width - VIEWPORT_MARGIN;
      this.left = Math.max(VIEWPORT_MARGIN, Math.min(anchorCenter - width / 2, maxLeft));
      this.arrowLeft = Math.max(ARROW_MARGIN, Math.min(anchorCenter - this.left, width - ARROW_MARGIN));
      this.positioned = true;
    },

    onDocumentMousedown(event) {
      if (!this.day) {
        return;
      }
      // a click on the anchor is handled by toggle()
      if ((this.$refs.popover && this.$refs.popover.contains(event.target)) || (this.anchor && this.anchor.contains(event.target))) {
        return;
      }
      this.close();
    },

    onKeydown(event) {
      if (this.day && (event.key === 'Escape' || event.keyCode === 27)) {
        this.close();
      }
    },

    edit() {
      const day = this.day;
      this.close();
      this.$emit('edit', day);
    }
  },

  filters: {
    datetime: function (datetime, format) {
      const date = moment(datetime);
      return date.isValid() ? date.format(format) : '';
    }
  }
};
</script>

<style lang="scss">
.day-flag-popover {
  position: fixed;
  z-index: 1060;
  width: 300px;
  max-width: calc(100vw - 16px);
  padding: 12px 14px;
  background: #FFFFFF;
  border: 1px solid #E4E7EA;
  border-radius: 8px;
  box-shadow: 0 6px 24px rgba(23, 28, 34, 0.14);
  color: #23282C;
  font-size: 13px;
  line-height: 1.4;
  text-align: left;
  white-space: normal;

  &__arrow {
    position: absolute;
    width: 12px;
    height: 12px;
    margin-left: -6px;
    background: #FFFFFF;
    border: 1px solid #E4E7EA;
    transform: rotate(45deg);
  }

  &--bottom &__arrow {
    top: -7px;
    border-right: 0;
    border-bottom: 0;
  }

  &--top &__arrow {
    bottom: -7px;
    border-left: 0;
    border-top: 0;
  }

  &__header {
    display: flex;
    align-items: flex-start;
    margin: 0 -14px 10px;
    padding: 0 14px 10px;
    border-bottom: 1px solid #F0F2F4;
  }

  &__icon {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    margin-right: 10px;
    border-radius: 6px;
    color: #FFFFFF;
    font-size: 12px;

    &--note {
      background-color: #FFC107;
    }

    &--info {
      background-color: #63C2DE;
    }

    &--danger {
      background-color: #F86C6B;
    }
  }

  &__heading {
    flex: 1 1 auto;
    min-width: 0;
  }

  &__title {
    font-weight: 600;
  }

  &__subtitle {
    color: #73818F;
    font-size: 12px;
  }

  &__close {
    flex: 0 0 auto;
    margin: -2px -4px 0 8px;
    padding: 2px 4px;
    border: 0;
    background: transparent;
    color: #73818F;
    font-size: 16px;
    line-height: 1;
    cursor: pointer;

    &:hover {
      color: #23282C;
    }
  }

  &__body {
    margin: 0 -6px;
    padding: 4px 6px;
    border-radius: 4px;
    white-space: pre-wrap;
    word-break: break-word;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 10px;
    padding-top: 8px;
    border-top: 1px solid #F0F2F4;
    color: #73818F;
    font-size: 12px;
  }

  &__actions {
    flex: 0 0 auto;
    margin-left: 8px;
    white-space: nowrap;
  }

  &__action {
    padding: 2px 5px;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: #20A8D8;
    cursor: pointer;

    &:hover {
      background-color: #E9F6FB;
    }
  }
}
</style>



// WEBPACK FOOTER //
// resources/assets/admin/js/attendance/show/DayFlagPopover.vue